/*
 * Planigo
 * Auteur  : Stéphane Raverdy
 * Société : SCIAD 2026
 */
import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { testsApi, projectsApi, requirementsApi, tasksApi, testsCsvTemplateUrl } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useAppConfig } from '../contexts/ConfigContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Plus, MagnifyingGlass, TestTube, Play, CheckCircle, XCircle,
  Warning, Minus, Camera, Eye, Trash, Pause, Image as ImageIcon, UploadSimple, Pencil, DownloadSimple, FilePdf, X
} from '@phosphor-icons/react';
import { formatDate, getLabel, getBadgeClass } from '../lib/utils';
import { toast } from 'sonner';

export default function TestsPage() {
  const { hasPermission } = useAuth();
  const { getList } = useAppConfig();
  const CRITICALITIES = getList('criticality', [{value:'minor',label:'Mineure'},{value:'major',label:'Majeure'},{value:'blocking',label:'Bloquante'}]);
  const [searchParams] = useSearchParams();
  const [tests, setTests] = useState([]);
  const [projects, setProjects] = useState([]);
  const [requirements, setRequirements] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [quality, setQuality] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [projectFilter, setProjectFilter] = useState(searchParams.get('project') || 'all');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [executingTest, setExecutingTest] = useState(null);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [importProjectId, setImportProjectId] = useState('');
  const [importFile, setImportFile] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [importingCSV, setImportingCSV] = useState(false);
  const [newTest, setNewTest] = useState({
    project_id: '', title: '', description: '', prerequisites: '',
    steps: [{ description: '', expected_result: '' }],
    criticality: 'major', requirement_ids: [], task_id: '', subtask_id: '',
  });
  const [editingTestId, setEditingTestId] = useState(null);

  useEffect(() => { fetchData(); }, [projectFilter]);

  useEffect(() => {
    const pid = newTest.project_id;
    if (!pid) { setRequirements([]); setTasks([]); return; }
    Promise.all([requirementsApi.getAll({ project_id: pid }), tasksApi.getAll({ project_id: pid })])
      .then(([r,t]) => { setRequirements(r.data || []); setTasks(t.data || []); })
      .catch(() => { setRequirements([]); setTasks([]); });
  }, [newTest.project_id]);

  const fetchData = async () => {
    try {
      const [testsRes, projectsRes, qualityRes] = await Promise.all([
        testsApi.getAll({ project_id: projectFilter !== 'all' ? projectFilter : undefined }),
        projectsApi.getAll(),
        testsApi.qualityDashboard(projectFilter !== 'all' ? projectFilter : ''),
      ]);
      setTests(testsRes.data);
      setProjects(projectsRes.data);
      setQuality(qualityRes.data || {});
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTest = async () => {
    try {
      const testData = {
        ...newTest,
        steps: newTest.steps.filter(s => s.description.trim()).map(s => ({
          step_description: s.description,
          expected_result: s.expected_result,
        })),
      };
      if (editingTestId) {
        await testsApi.update(editingTestId, testData);
        toast.success('Scenario mis a jour');
      } else {
        await testsApi.create(testData);
        toast.success('Scenario cree');
      }
      setIsCreateDialogOpen(false);
      setEditingTestId(null);
      setNewTest({ project_id: '', title: '', description: '', prerequisites: '', steps: [{ description: '', expected_result: '' }], criticality: 'major', requirement_ids: [], task_id: '', subtask_id: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const openEditTest = (test) => {
    setNewTest({
      project_id: test.project_id, title: test.title, description: test.description || '',
      prerequisites: test.prerequisites || '',
      steps: (test.steps || []).map(s => ({ description: s.step_description || s.description || '', expected_result: s.expected_result || '' })),
      criticality: test.criticality || 'major',
      requirement_ids: test.requirement_ids || [], task_id: test.task_id || '', subtask_id: test.subtask_id || '',
    });
    setEditingTestId(test.id);
    setIsCreateDialogOpen(true);
  };

  const addStep = () => {
    setNewTest(prev => ({ ...prev, steps: [...prev.steps, { description: '', expected_result: '' }] }));
  };

  const updateStepField = (index, field, value) => {
    setNewTest(prev => ({
      ...prev,
      steps: prev.steps.map((s, i) => i === index ? { ...s, [field]: value } : s),
    }));
  };

  const removeStep = (index) => {
    setNewTest(prev => ({ ...prev, steps: prev.steps.filter((_, i) => i !== index) }));
  };

  const openExecutionView = async (test) => {
    try {
      const res = await testsApi.getById(test.id);
      setExecutingTest(res.data);
    } catch {
      toast.error('Erreur');
    }
  };

  const handleExecuteStep = async (stepIndex, status, notes, actualResult) => {
    if (!executingTest) return;
    try {
      await testsApi.executeStep(executingTest.id, {
        step_index: stepIndex,
        status,
        notes: notes || '',
        actual_result: actualResult || '',
      });
      toast.success(`Etape ${stepIndex + 1} mise a jour`);
      const res = await testsApi.getById(executingTest.id);
      setExecutingTest(res.data);
      fetchData();
    } catch {
      toast.error('Erreur');
    }
  };

  const handleUploadScreenshot = async (stepIndex, file) => {
    if (!executingTest || !file) return;
    try {
      await testsApi.uploadScreenshot(executingTest.id, stepIndex, file);
      toast.success('Capture ajoutee');
      const res = await testsApi.getById(executingTest.id);
      setExecutingTest(res.data);
    } catch {
      toast.error("Erreur lors de l'upload");
    }
  };

  const handleImportTestCSV = async () => {
    if (!importFile || !importProjectId) return;
    setImportingCSV(true);
    try {
      const res = /\.xlsx?$/i.test(importFile.name) ? await testsApi.importExcel(importProjectId, importFile) : await testsApi.importCSV(importProjectId, importFile);
      setImportResult(res.data);
      toast.success(`${res.data.created} scenario(s) importe(s)`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur import');
    } finally {
      setImportingCSV(false);
    }
  };

  const filteredTests = tests.filter(test =>
    test.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const stats = {
    total: tests.length,
    closed: tests.filter(t => t.status === 'closed').length,
    toReview: tests.filter(t => t.status === 'to_review').length,
    notExecuted: tests.filter(t => !t.status || t.status === 'not_executed').length,
  };

  return (
    <div className="p-6 lg:p-8 space-y-6 animate-fade-in" data-testid="tests-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-heading font-extrabold text-gradient">Tests & QA</h1>
          <p className="text-slate-600 mt-1">Scenarios de test avec execution pas-a-pas</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => { setIsImportDialogOpen(true); setImportResult(null); }} data-testid="import-test-csv-btn">
            <UploadSimple size={18} className="mr-2" />Importer scenarios
          </Button>
          <a href={testsApi.exportExcelUrl(projectFilter !== 'all' ? projectFilter : '')}><Button variant="outline"><DownloadSimple size={17} className="mr-1"/>Excel</Button></a>
          <a href={testsApi.exportPdfUrl(projectFilter !== 'all' ? projectFilter : '')}><Button variant="outline"><FilePdf size={17} className="mr-1"/>PDF</Button></a>
          <Dialog open={isCreateDialogOpen} onOpenChange={(o) => { setIsCreateDialogOpen(o); if (!o) { setEditingTestId(null); setNewTest({ project_id: '', title: '', description: '', prerequisites: '', steps: [{ description: '', expected_result: '' }], criticality: 'major', requirement_ids: [], task_id: '', subtask_id: '' }); } }}>
            {hasPermission('tests:create') && <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="create-test-btn">
                <Plus size={18} className="mr-2" />Nouveau scenario
              </Button>
            </DialogTrigger>}
          <DialogContent className="dialog-wide max-h-[92vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Creer un scenario de test</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Projet *</Label>
                  <Select value={newTest.project_id} onValueChange={(v) => setNewTest(p => ({ ...p, project_id: v }))}>
                    <SelectTrigger data-testid="test-project-select"><SelectValue placeholder="Selectionner" /></SelectTrigger>
                    <SelectContent>
                      {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.code} - {p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Criticite</Label>
                  <Select value={newTest.criticality} onValueChange={(v) => setNewTest(p => ({ ...p, criticality: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{CRITICALITIES.map(x=><SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              {newTest.project_id && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Exigence couverte</Label>
                    <Select value={(newTest.requirement_ids || [])[0] || 'none'} onValueChange={(v) => setNewTest(p => ({ ...p, requirement_ids: v === 'none' ? [] : [v] }))}>
                      <SelectTrigger><SelectValue placeholder="Aucune" /></SelectTrigger>
                      <SelectContent><SelectItem value="none">Aucune</SelectItem>{requirements.map(r => <SelectItem key={r.id} value={r.id}>{r.code || 'EXG'} - {r.title}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Tache</Label>
                    <Select value={newTest.task_id || 'none'} onValueChange={(v) => setNewTest(p => ({ ...p, task_id: v === 'none' ? '' : v, subtask_id: '' }))}>
                      <SelectTrigger><SelectValue placeholder="Aucune" /></SelectTrigger>
                      <SelectContent><SelectItem value="none">Aucune</SelectItem>{tasks.filter(t => !t.parent_task_id).map(t => <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Sous-tache</Label>
                    <Select value={newTest.subtask_id || 'none'} onValueChange={(v) => setNewTest(p => ({ ...p, subtask_id: v === 'none' ? '' : v }))} disabled={!newTest.task_id}>
                      <SelectTrigger><SelectValue placeholder="Aucune" /></SelectTrigger>
                      <SelectContent><SelectItem value="none">Aucune</SelectItem>{tasks.filter(t => t.parent_task_id === newTest.task_id).map(t => <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
              )}
              <div className="space-y-2">
                <Label>Titre *</Label>
                <Input value={newTest.title} onChange={(e) => setNewTest(p => ({ ...p, title: e.target.value }))} placeholder="Titre du scenario" data-testid="test-title-input" />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea value={newTest.description} onChange={(e) => setNewTest(p => ({ ...p, description: e.target.value }))} rows={2} />
              </div>
              <div className="space-y-2">
                <Label>Prerequis</Label>
                <Textarea value={newTest.prerequisites} onChange={(e) => setNewTest(p => ({ ...p, prerequisites: e.target.value }))} rows={2} />
              </div>

              {/* Steps */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold">Etapes de test</Label>
                  <Button variant="outline" size="sm" onClick={addStep}>
                    <Plus size={14} className="mr-1" />Ajouter
                  </Button>
                </div>
                {newTest.steps.map((step, i) => (
                  <div key={i} className="p-4 bg-slate-50 rounded-lg space-y-3 relative">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-slate-700">Etape {i + 1}</span>
                      {newTest.steps.length > 1 && (
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => removeStep(i)}>
                          <Trash size={14} />
                        </Button>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Action du test *</Label>
                      <Input value={step.description} onChange={(e) => updateStepField(i, 'description', e.target.value)} placeholder="Action a effectuer..." />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Resultat attendu</Label>
                      <Input value={step.expected_result} onChange={(e) => updateStepField(i, 'expected_result', e.target.value)} placeholder="Ce qui devrait se passer..." />
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>Annuler</Button>
                <Button className="btn-primary" onClick={handleCreateTest} disabled={!newTest.project_id || !newTest.title || !newTest.steps.some(s => s.description.trim())} data-testid="submit-test-btn">
                  Creer
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* CSV Import Dialog */}
      <Dialog open={isImportDialogOpen} onOpenChange={setIsImportDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="import-test-csv-dialog">
          <DialogHeader>
            <DialogTitle>Importer des scenarios de test</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Projet *</Label>
              <Select value={importProjectId} onValueChange={setImportProjectId}>
                <SelectTrigger><SelectValue placeholder="Selectionner le projet" /></SelectTrigger>
                <SelectContent>
                  {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.code} - {p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Fichier CSV ou Excel *</Label>
              <Input type="file" accept=".csv,.xlsx,.xls" onChange={(e) => setImportFile(e.target.files?.[0] || null)} />
            </div>
            <div className="bg-slate-50 p-3 rounded-lg text-xs text-slate-600 space-y-1">
              <p className="font-semibold">Colonnes du modele :</p>
              <p>titre · description · prerequis · criticite · etape · resultat_attendu</p>
              <p className="text-slate-500">Meme titre = meme scenario, chaque ligne ajoute une etape.</p>
              <div className="flex gap-3 pt-1"><a href={testsApi.xlsxTemplateUrl} className="inline-flex items-center gap-1 text-blue-600 font-medium"><DownloadSimple size={12}/>Modele Excel</a><a href={testsCsvTemplateUrl} className="inline-flex items-center gap-1 text-blue-600 font-medium"><DownloadSimple size={12}/>Modele CSV</a></div>
            </div>
            {importResult && (
              <div className={`p-3 rounded-lg text-sm ${importResult.errors?.length > 0 ? 'bg-amber-50 text-amber-800' : 'bg-green-50 text-green-800'}`}>
                <p className="font-semibold">{importResult.created} scenario(s) importe(s)</p>
                {importResult.errors?.length > 0 && (
                  <ul className="mt-1 space-y-0.5 text-xs">
                    {importResult.errors.map((err, i) => <li key={i}>- {err}</li>)}
                  </ul>
                )}
              </div>
            )}
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => { setIsImportDialogOpen(false); setImportResult(null); }}>Fermer</Button>
              <Button className="btn-primary" onClick={handleImportTestCSV} disabled={!importProjectId || !importFile || importingCSV}>
                {importingCSV ? 'Import en cours...' : 'Importer'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dashboard qualite */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
        {[
          ['Couverture exigences', `${quality.coverage_rate ?? 0}%`, 'text-blue-600'],
          ['Exigences non couvertes', quality.requirements_uncovered ?? 0, 'text-orange-600'],
          ['Tests', quality.tests_total ?? stats.total, 'text-slate-900'],
          ['Reussite', `${quality.success_rate ?? 0}%`, 'text-emerald-600'],
          ['Bloques', quality.tests_blocked ?? 0, 'text-red-600'],
          ['A retester', quality.tests_to_retest ?? stats.toReview, 'text-violet-600'],
        ].map(([label,value,color]) => (
          <Card key={label} className="card-surface p-3">
            <p className="text-[11px] text-slate-500">{label}</p><p className={`text-xl font-bold ${color}`}>{value}</p>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Rechercher..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" data-testid="search-tests-input" />
        </div>
        <Select value={projectFilter} onValueChange={setProjectFilter}>
          <SelectTrigger className="w-52"><SelectValue placeholder="Tous les projets" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les projets</SelectItem>
            {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.code} - {p.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Tests List */}
      <Card className="card-surface">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 space-y-3">
              {[...Array(5)].map((_, i) => <div key={i} className="h-16 bg-slate-200 rounded animate-pulse" />)}
            </div>
          ) : filteredTests.length === 0 ? (
            <div className="py-16 text-center">
              <TestTube size={48} className="mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Aucun scenario</h3>
              <p className="text-slate-500">Creez votre premier scenario de test</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {filteredTests.map(test => {
                const project = projects.find(p => p.id === test.project_id);
                const totalSteps = test.steps?.length || 0;
                const okSteps = test.steps?.filter(s => s.status === 'ok').length || 0;
                const koSteps = test.steps?.filter(s => s.status === 'ko').length || 0;
                return (
                  <div key={test.id} className="flex items-center gap-4 p-4 hover:bg-slate-50 transition-colors" data-testid={`test-row-${test.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-mono text-slate-500">{project?.code}</span>
                        <p className="font-medium text-slate-900 truncate">{test.title}</p>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-slate-500">
                        <span>{totalSteps} etape(s)</span>
                        {totalSteps > 0 && (
                          <>
                            <span className="text-emerald-600 font-medium">{okSteps} OK</span>
                            {koSteps > 0 && <span className="text-red-600 font-medium">{koSteps} KO</span>}
                          </>
                        )}
                        <span>v{test.version}</span>
                      </div>
                    </div>
                    <Badge className={getBadgeClass(test.criticality, 'criticality')}>
                      {getLabel(test.criticality, 'criticality')}
                    </Badge>
                    <Badge className={getBadgeClass(test.status || 'not_executed', 'status')}>
                      {getLabel(test.status || 'not_executed', 'status')}
                    </Badge>
                    {hasPermission('tests:update') && (
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-blue-600 hover:text-blue-700" onClick={() => openEditTest(test)} data-testid={`edit-test-${test.id}`}>
                        <Pencil size={14} />
                      </Button>
                    )}
                    <Button variant="outline" size="sm" onClick={() => openExecutionView(test)} data-testid={`execute-test-${test.id}`}>
                      <Play size={14} className="mr-1" />Executer
                    </Button>
                    {hasPermission('tests:delete') && (
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-700" onClick={async () => {
                        if (!window.confirm('Supprimer ce scenario ?')) return;
                        try { await testsApi.delete(test.id); toast.success('Supprime'); fetchData(); } catch { toast.error('Erreur'); }
                      }} data-testid={`delete-test-${test.id}`}>
                        <Trash size={14} />
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Execution plein ecran */}
      {executingTest && (
        <div className="fixed inset-0 z-[100] bg-[var(--app-bg)] overflow-y-auto" data-testid="test-execution-page">
          <div className="sticky top-0 z-10 bg-[var(--app-surface)] border-b border-[var(--app-border)] px-5 py-3 flex items-center justify-between shadow-sm">
            <div className="flex items-center gap-3"><TestTube size={22} className="text-blue-600"/><div><h2 className="font-bold text-lg">Execution : {executingTest.title}</h2><p className="text-xs text-slate-500">Historique conserve a chaque etape · v{executingTest.version}</p></div></div>
            <div className="flex gap-2"><a href={testsApi.exportExcelUrl(executingTest.project_id)}><Button variant="outline" size="sm"><DownloadSimple size={15} className="mr-1"/>Excel</Button></a><a href={testsApi.exportPdfUrl(executingTest.project_id)}><Button variant="outline" size="sm"><FilePdf size={15} className="mr-1"/>PDF</Button></a><Button variant="outline" size="sm" onClick={()=>setExecutingTest(null)}><X size={16} className="mr-1"/>Fermer</Button></div>
          </div>
          <div className="p-5 lg:p-8 max-w-[1600px] mx-auto space-y-5">
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-5">
              <div className="space-y-4">
                {executingTest.description && <p className="text-sm bg-[var(--app-surface)] border rounded-lg p-3">{executingTest.description}</p>}
                {executingTest.prerequisites && <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg"><p className="text-sm font-medium text-amber-800">Prerequis</p><p className="text-sm text-amber-700">{executingTest.prerequisites}</p></div>}
                <div className="space-y-4">{executingTest.steps?.map((step,i)=><StepExecutionRow key={i} step={step} stepIndex={i} onExecute={handleExecuteStep} onUploadScreenshot={handleUploadScreenshot}/>)}</div>
              </div>
              <Card className="card-surface h-fit lg:sticky lg:top-20"><CardHeader><CardTitle className="text-base">Synthese & historique</CardTitle></CardHeader><CardContent className="space-y-3"><div className="flex justify-between text-sm"><span>Statut global</span><Badge className={getBadgeClass(executingTest.status || 'not_executed','status')}>{getLabel(executingTest.status || 'not_executed','status')}</Badge></div><div className="text-xs text-slate-500">{executingTest.executions?.length || 0} action(s) historisee(s)</div><div className="max-h-[60vh] overflow-y-auto space-y-2">{[...(executingTest.executions||[])].reverse().map((e,i)=><div key={i} className="border rounded p-2 text-xs"><div className="font-semibold">Etape {(e.step_index ?? 0)+1} · {String(e.status||'').toUpperCase()}</div><div className="text-slate-500">{e.executed_at ? new Date(e.executed_at).toLocaleString('fr-FR') : ''}</div>{e.notes&&<div className="mt-1">{e.notes}</div>}</div>)}</div></CardContent></Card>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StepExecutionRow({ step, stepIndex, onExecute, onUploadScreenshot }) {
  const [notes, setNotes] = useState(step.notes || '');
  const [actualResult, setActualResult] = useState(step.actual_result || '');
  const [showScreenshot, setShowScreenshot] = useState(false);

  const statusBg = {
    ok: 'bg-green-50 border-green-300',
    ko: 'bg-red-50 border-red-300',
    blocked: 'bg-amber-50 border-amber-300',
    paused: 'bg-purple-50 border-purple-300',
    pending: 'bg-white border-slate-200',
  };

  const statusIcon = {
    ok: <CheckCircle size={20} className="text-green-600" weight="fill" />,
    ko: <XCircle size={20} className="text-red-600" weight="fill" />,
    blocked: <Warning size={20} className="text-amber-600" weight="fill" />,
    paused: <Pause size={20} className="text-purple-600" weight="fill" />,
    pending: <Minus size={20} className="text-slate-400" />,
  };

  return (
    <div className={`rounded-lg border-2 ${statusBg[step.status || 'pending']} overflow-hidden`} data-testid={`test-step-${stepIndex}`}>
      {/* Step header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-100/50">
        <span className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-sm font-bold text-slate-700 flex-shrink-0">
          {stepIndex + 1}
        </span>
        {statusIcon[step.status || 'pending']}
        <div className="flex-1">
          <p className="font-semibold text-slate-900 text-sm">Action du test</p>
          <p className="text-slate-700">{step.step_description}</p>
        </div>
      </div>

      <div className="p-4 space-y-3">
        {/* Expected result - always visible */}
        <div className="bg-white/60 rounded-lg p-3">
          <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Resultat Attendu</p>
          <p className="text-sm text-slate-700">{step.expected_result || <span className="italic text-slate-400">Non defini</span>}</p>
        </div>

        {/* Actual result - editable */}
        <div>
          <Label className="text-xs font-semibold text-slate-500 uppercase">Resultat Obtenu</Label>
          <Textarea
            value={actualResult}
            onChange={(e) => setActualResult(e.target.value)}
            placeholder="Decrire le resultat obtenu lors de l'execution..."
            rows={2}
            className="mt-1 text-sm"
            data-testid={`step-actual-result-${stepIndex}`}
          />
        </div>

        {/* Notes */}
        <div>
          <Label className="text-xs font-semibold text-slate-500 uppercase">Notes</Label>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Notes complementaires..."
            rows={1}
            className="mt-1 text-sm"
          />
        </div>

        {/* Status buttons */}
        <div className="flex items-center gap-2 flex-wrap pt-1">
          <Button size="sm" className={`${step.status === 'ok' ? 'bg-green-600 ring-2 ring-green-300' : 'bg-green-500 hover:bg-green-600'} text-white`}
            onClick={() => onExecute(stepIndex, 'ok', notes, actualResult)} data-testid={`step-ok-${stepIndex}`}>
            <CheckCircle size={14} className="mr-1" />OK
          </Button>
          <Button size="sm" className={`${step.status === 'ko' ? 'bg-red-600 ring-2 ring-red-300' : 'bg-red-500 hover:bg-red-600'} text-white`}
            onClick={() => onExecute(stepIndex, 'ko', notes, actualResult)} data-testid={`step-ko-${stepIndex}`}>
            <XCircle size={14} className="mr-1" />KO
          </Button>
          <Button size="sm" className={`${step.status === 'paused' ? 'bg-purple-600 ring-2 ring-purple-300' : 'bg-purple-500 hover:bg-purple-600'} text-white`}
            onClick={() => onExecute(stepIndex, 'paused', notes, actualResult)} data-testid={`step-paused-${stepIndex}`}>
            <Pause size={14} className="mr-1" />En pause
          </Button>
          <Button size="sm" variant={step.status === 'blocked' ? 'default' : 'outline'}
            onClick={() => onExecute(stepIndex, 'blocked', notes, actualResult)} data-testid={`step-blocked-${stepIndex}`}>
            <Warning size={14} className="mr-1" />Bloque
          </Button>

          <div className="ml-auto flex items-center gap-2">
            {/* Screenshot upload */}
            <Button size="sm" variant="outline" className="gap-1" onClick={() => {
              const input = document.createElement('input');
              input.type = 'file';
              input.accept = 'image/*';
              input.multiple = true;
              input.onchange = async (e) => {
                const files = Array.from(e.target.files || []);
                for (const file of files) {
                  await onUploadScreenshot(stepIndex, file);
                }
              };
              input.click();
            }} data-testid={`step-screenshot-btn-${stepIndex}`}>
              <Camera size={14} />Ajouter image(s)
            </Button>
          </div>
        </div>

        {/* Screenshot previews - support multiple images */}
        {(step.screenshot_urls?.length > 0 || step.screenshot_url) && (
          <div className="mt-2 space-y-2">
            <div className="flex items-center gap-2">
              <ImageIcon size={14} className="text-blue-600" />
              <span className="text-xs font-medium text-blue-600">
                {(step.screenshot_urls || [step.screenshot_url]).filter(Boolean).length} capture(s) d'ecran
              </span>
              <Button size="sm" variant="ghost" className="h-5 text-[10px] px-1" onClick={() => setShowScreenshot(!showScreenshot)}>
                {showScreenshot ? 'Masquer' : 'Afficher'}
              </Button>
            </div>
            {showScreenshot && (
              <div className="grid grid-cols-2 gap-2">
                {(step.screenshot_urls || [step.screenshot_url]).filter(Boolean).map((url, imgIdx) => (
                  <div key={imgIdx} className="border rounded-lg overflow-hidden bg-white relative group">
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}${url}`}
                      alt={`Capture etape ${stepIndex + 1} - ${imgIdx + 1}`}
                      className="max-w-full max-h-[200px] object-contain mx-auto"
                      data-testid={`step-screenshot-preview-${stepIndex}-${imgIdx}`}
                    />
                    <a href={`${process.env.REACT_APP_BACKEND_URL}${url}`} target="_blank" rel="noopener noreferrer"
                      className="absolute top-1 right-1 bg-white/80 rounded px-1.5 py-0.5 text-[10px] text-blue-600 hover:underline opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-0.5">
                      <Eye size={10} />Plein ecran
                    </a>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Existing actual result display if not editing */}
        {step.actual_result && !actualResult && (
          <div className="bg-white/60 rounded p-2 text-xs text-slate-600">
            <span className="font-medium">Resultat obtenu:</span> {step.actual_result}
          </div>
        )}
      </div>
    </div>
  );
}
